import { createContext, useCallback, useMemo, useReducer } from 'react';
import { getResourceFiltersInitialState, resourceFiltersReducer } from './reducer';
import { FavoriteResource, Filters, Resource, ResourceActionType, ResourceFilter } from './types';
import { filterResourceByTag, filterResourcesByText } from '../../util/resources-util';

export const ResourceFiltersContext = createContext<any>({});

const ResourceFilterProvider = ({ children }) => {
  const [state, dispatch] = useReducer(resourceFiltersReducer, getResourceFiltersInitialState());

  const setResourceFilters = useCallback(
    (resourceFilters: Filters[]) => {
      dispatch({ type: ResourceActionType.SET_FILTERS, payload: resourceFilters });
    },
    [dispatch]
  );

  const setResources = useCallback(
    (resources: Resource[]) => {
      dispatch({ type: ResourceActionType.SET_RESOURCES, payload: resources });
    },
    [dispatch]
  );

  const removeResource = useCallback(
    (resource: Resource) => {
      dispatch({ type: ResourceActionType.REMOVE_RESOURCE, payload: resource });
    },
    [dispatch]
  );

  const setSelectedFilters = useCallback(
    (selectedFilters: ResourceFilter[]) => {
      dispatch({ type: ResourceActionType.SET_SELECTED_FILTER, payload: selectedFilters });
    },
    [dispatch]
  );

  const setResourcesSearchText = useCallback(
    (text: string) => {
      dispatch({
        type: ResourceActionType.SET_RESOURCES_SEARCH_TEXT,
        payload: text,
      });
    },
    [dispatch]
  );

  const removeAllResourceFilters = useCallback(() => {
    dispatch({
      type: ResourceActionType.REMOVE_ALL_FILTERS,
      payload: null,
    });
  }, [dispatch]);

  const setPinnedResources = useCallback(
    (pinnedResources: FavoriteResource[]) => {
      dispatch({ type: ResourceActionType.SET_PINNED_RESOURCES, payload: pinnedResources });
    },
    [dispatch]
  );

  const addPinnedResource = useCallback(
    (resource: FavoriteResource) => {
      dispatch({
        type: ResourceActionType.ADD_PINNED_RESOURCE,
        payload: resource,
      });
    },
    [dispatch]
  );

  const removePinnedResource = useCallback(
    (resourceId: number) => {
      dispatch({
        type: ResourceActionType.REMOVE_PINNED_RESOURCE,
        payload: resourceId,
      });
    },
    [dispatch]
  );

  const setPinActions = useCallback(
    (pinAction: string) => {
      dispatch({
        type: ResourceActionType.SET_PIN_ACTIONS,
        payload: pinAction,
      });
    },
    [dispatch]
  );

  const filteredResources = useMemo(() => {
    let filteredResources = state.resourcesSearchText
      ? Array.from(filterResourcesByText(state.resources, state.resourcesSearchText))
      : state.resources;

    if (state.selectedFilters.length) {
      filteredResources = filteredResources.filter((resource: any) => {
        return filterResourceByTag(resource, state.selectedFilters);
      });
    }

    return filteredResources;
  }, [state.selectedFilters, state.resourcesSearchText, state.resources]);

  return (
    <ResourceFiltersContext.Provider
      value={[
        {
          ...state,
          filteredResources,
        },
        {
          setResourceFilters,
          setResources,
          removeResource,
          setResourcesSearchText,
          removeAllResourceFilters,
          setSelectedFilters,
          setPinnedResources,
          addPinnedResource,
          removePinnedResource,
          setPinActions,
        },
      ]}
    >
      {children}
    </ResourceFiltersContext.Provider>
  );
};

export default ResourceFilterProvider;
